import { AxiosResponse } from 'axios';
import { defineStore } from 'pinia';

import api from '@/api/api';
import { ALERTS_UPDATE_URL, ALERTS_URL } from '@/constants/urls';
import { LOG_PREFIX } from '@/types/enums';
import { AlertsResponse, AlertWrapper } from '@/types/responseTypes';

interface State {
  alerts: AlertWrapper[] | null
  unreadAlerts: number
  selectedAlertId: number | null
  isLoading: boolean
  isError: boolean
  hasLoaded: boolean
}

export default defineStore('alerts', {
  state: (): State => ({
    alerts: null,
    unreadAlerts: 0,
    selectedAlertId: null,
    isLoading: false,
    isError: false,
    hasLoaded: false,
  }),
  getters: {
    unreadAlertsCount(state): number {
      return state.unreadAlerts;
    },
    selectedAlert(state): AlertWrapper | undefined {
      if (state.selectedAlertId === null) return undefined;
      return state.alerts?.find((alert) => alert.alertId === state.selectedAlertId);
    },
    snackbarAlert(state): AlertWrapper | false {
      const alert = [...(state.alerts ?? [])]
        .filter((currentAlert) => !currentAlert.viewed)
        .sort((a, b) => new Date(b.reportedOn).getTime() - new Date(a.reportedOn).getTime())[0];

      return alert ?? false;
    },
  },
  actions: {
    async fetchAlerts(): Promise<void> {
      this.isLoading = true;
      this.isError = false;

      try {
        const response = await api({
          url: ALERTS_URL,
          method: 'get',
          logPrefix: LOG_PREFIX.ALERTS,
        }) as AxiosResponse<AlertsResponse>;

        if (response.data) {
          this.alerts = response.data.alerts;
          this.unreadAlerts = response.data.unreadAlerts;
        }
      } catch (error) {
        this.isError = true;
        window.$log?.warn(`[${LOG_PREFIX.ALERTS}]: Failed to fetch alerts - ${error}`);
      } finally {
        this.hasLoaded = true;
        this.isLoading = false;
      }
    },
    async markAlertAsViewed(alertId: number): Promise<void> {
      const alert = this.alerts?.find((currentAlert) => currentAlert.alertId === alertId);

      if (!alert || alert.viewed) return;

      try {
        await api({
          url: `${ALERTS_UPDATE_URL}/${alertId}`,
          method: 'post',
          logPrefix: LOG_PREFIX.ALERTS,
        });

        alert.viewed = true;
        this.unreadAlerts = Math.max(this.unreadAlerts - 1, 0);
      } catch {
        window.$log?.warn(`[${LOG_PREFIX.ALERTS}]: Failed to mark alert as viewed`);
      }
    },
    openAlertDetail(alertId: number): void {
      this.selectedAlertId = alertId;
    },
    closeAlertDetail(): void {
      this.selectedAlertId = null;
    },
  },
});
